<template>
    <div>
        <transition name="fade">
            <GuideVideoBanner
                v-if="guideData && showGuideCard"
                :text="guideData.content.text"
                :subtext="guideData.content.subText"
                :videoUrl="guideData.content.videoUrl"
                :videoThumbnail="guideData.content.videoThumbnail"
                @close="handleGuideClose"
            />
        </transition>
        <ExGenericBanner
            v-if="bannerData.title"
            :title="bannerData.title"
            :description="bannerData.description"
            :buttonText="bannerData.buttonText"
            :icon="bannerData.icon"
            style="margin: 14px 0px;"
        />
        <div class="your-employees-to-inspect-tab-wrapper mt-2" :class="{ 'has-data': hasAnyData }">
            <NoRecordFound
                v-if="!hasAnyData"
                :should-show-add-action="false"
                description="There are no Employees with Assigned Responsibilities that you are a coach of"
                class="no-record-found"
                :noBorder="true"
                :showActiveCounter="false"
                showSpecificMessage="You currently have no Employees to Inspect"
                :showClearAction="false"
                message="Employees"
                btnTitle="Employee"
                @onClear="() => {}"
            />
            <YourEmployeesToInspectTable 
                v-else
                :employeesData="employees" 
                :showPagination="showPagination" 
                :showNoRecords="employees.length === 0 && searchQuery === ''"
                :pagination="{ total: totalCount, page: currentPage, perPage: perPage }"
                :filterText="searchQuery"
                :chipText="chipText"
                :defaultSelected="badgeFilters"
                @onSearch="handleSearch"
                @onPaginationRequest="handlePaginationRequest"
                @onIssueBadgeClick="handleIssueBadgeClick($event)"
                @onBadgeRemoved="handleBadgeRemoved"
                @checkedItemsChanged="handleCheckedItemsChanged"
                @removeFilter="handleRemoveFilter"
                @clearSelection="handleClearSelection"
            />
            <EmployeeModal
                :showStep="employeeModalStep"
                :dialog="showEmployeeModal"
                @onAction="handleGiveBadge($event)"
                @back="closeEmployeeModal"
                @removeBadge="handleRemoveBadge"
                label="Responsibility"
                :name="selectedResponsibility?.name"
                :items="[]"
                :activeCheckpoint="activeCheckpoints"
                :value="[1]"
                :enableEmployeePagination="true"
                :empForceBadgeClass="true"
                :responsibility="selectedResponsibility"
                :employee="selectedEmployee"
                :hideEmployeePicker="true"
                :empName="selectedEmployee?.fullName"
            />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, getCurrentInstance, nextTick } from 'vue';
import YourEmployeesToInspectTable from '@components/YourEmployeesToInspectTable/YourEmployeesToInspectTable.vue';
import NoRecordFound from '@components/NoRecordFound/NoRecordFound.vue';
import EmployeeModal from '@components/EmployeeModal/EmployeeModal.vue';
import { getEmployeesToInspect } from '@/api/employeeAPI';
import { createProficiencyBadge } from '@/api/responsibilityAPI';
import { useFiltersStore } from '@/store/pinia/filters';
import ExGenericBanner from '@components/GenericBanner/ExGenericBanner.vue';
import GuideVideoBanner from '@components/GuideVideoBanner/GuideVideoBanner.vue';
import Knock, { KnockGuideClient } from "@knocklabs/client";
import configService from "../../account/src/services/config";

const props = defineProps({
    mockGuideData: {
        type: Object,
        default: null,
    },
    mockData: {
        type: Array,
        default: null,
    },
});

const { proxy } = getCurrentInstance();
const filtersStore = useFiltersStore();

// Filter key for localStorage persistence
const BADGE_FILTERS_KEY = 'inspectionBadgeFilters';

const employees = ref([]);
const perPage = ref(10);
const totalCount = ref(0);
const currentPage = ref(1);
const showPagination = ref(false);
const searchQuery = ref("");
const badgeFilters = ref([]);
let searchTimeout = null;
const bannerData = ref({
    title: null,
    description: null,
    buttonText: null,
    icon: null,
});

const hasAnyData = computed(() => {
    if (searchQuery.value !== "") return true;
    return totalCount.value > 0 || employees.value.length > 0;
});

// Computed property for chip text display
const chipText = computed(() => {
    return badgeFilters.value.map(filter => 
        filter.charAt(0).toUpperCase() + filter.slice(1)
    );
});

const showEmployeeModal = ref(false);
const employeeModalStep = ref(2);
const activeCheckpoints = ref(0);
const selectedEmployee = ref(null);
const selectedResponsibility = ref(null);
const currentAction = ref('');

// Guide banner state
const guideData = ref(null);
const showGuideCard = ref(true);
const knockClient = ref(null);
const guideClient = ref(null);
const guides = ref([]);

// Default guide data fallback with realistic dummy content
const defaultGuideData = {
    content: {
        text: "Welcome to the Coach view",
        subText: "Learn how to inspect and guide your employees",
        videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
        videoThumbnail: "https://i.postimg.cc/Qt9rwjJg/video-capture-t0239-98seg-1544.png",
    },
    markAsArchived: async () => {},
};

const initializeKnock = async () => {
    try {
        const authUser = JSON.parse(localStorage.getItem("user"));
        const apiKey = configService.get("knockApiKey");
        const userId = authUser?.id?.toString();
        const channelId = configService.get("knockGuideChannelId");

        if (!apiKey || !userId || !channelId) {
            // If Knock is not configured, use default guide data
            guideData.value = defaultGuideData;
            return;
        }

        knockClient.value = new Knock(apiKey);
        knockClient.value.authenticate({ id: userId });

        guideClient.value = new KnockGuideClient(knockClient.value, channelId);

        guideClient.value.store.subscribe(() => {
            const storeState = guideClient.value.store.state;
            guides.value = guideClient.value.selectGuides(storeState) || [];
            const coachGuide = guides.value.find(
                (guide) => guide.key === "what-is-expected-of-you-as-a-coach"
            );
            // Use Knock guide data if available, otherwise fall back to default
            guideData.value = coachGuide?.steps?.[0] || defaultGuideData;
        });

        await guideClient.value.subscribe();
        await guideClient.value.fetch();
        
        // If no guide data was found after fetching, use default
        if (!guideData.value) {
            guideData.value = defaultGuideData;
        }
    } catch (error) {
        console.error("Error initializing Knock:", error);
        // Use default guide data if Knock initialization fails
        guideData.value = defaultGuideData;
    }
};

const handleGuideClose = async () => {
    showGuideCard.value = false;
    
    if (guideData.value && guideData.value.markAsArchived) {
        try {
            await guideData.value.markAsArchived();
        } catch (error) {
            console.error('Error archiving guide in Knock:', error);
        }
    }
};

onMounted(async () => {
    filtersStore.loadFilters(BADGE_FILTERS_KEY);
    badgeFilters.value = filtersStore.filters || [];
    
    // Use mock guide data if provided (for Storybook), otherwise initialize Knock
    if (props.mockGuideData) {
        guideData.value = props.mockGuideData;
    } else {
        await initializeKnock();
        // Ensure guide data is set (fallback to default if Knock didn't provide any)
        if (!guideData.value) {
            guideData.value = defaultGuideData;
        }
    }
    
    // Use mock data if provided (for Storybook), otherwise fetch from API
    if (props.mockData) {
        employees.value = props.mockData;
        totalCount.value = props.mockData.length;
        showPagination.value = props.mockData.length > perPage.value;
        if (props.mockData.length > 0) {
            bannerData.value = {
                title: `You currently have ${props.mockData.length} potential ${props.mockData.length === 1 ? 'responsibility' : 'responsibilities'} to inspect`,
                description: 'When employees are ready for inspection, they should reach out to you to schedule it.',
                buttonText: null,
                icon: 'info_icon',
            };
        }
    } else {
        fetchEmployeesToInspect();
    }
});

const extractCodeNumber = (codeStr) => {
    const match = codeStr?.match(/#(\d+)/);
    return match ? match[1] : null;
};

const fetchEmployeesToInspect = async () => {
    try {
        proxy.$loader.show();
        const accountID = localStorage.getItem("currentAccountID");
        
        let response = await getEmployeesToInspect(
            accountID,
            perPage.value,
            (currentPage.value - 1) * perPage.value,
            searchQuery.value,
            false,
            "name",
            badgeFilters.value
        );

        if (response?.data) {
            const results = response.data.results || [];
            const transformedData = [];

            results.forEach(item => {
                if (item.first_name && item.last_name) {
                    const currentBadge = item.badges && item.badges.length > 0 
                        ? item.badges.find(b => b.is_current) || item.badges[0]
                        : null;
                    
                    const rawRespCode = item.responsibilityAssignments?.responsibility_code || '';
                    const cleanRespCode = extractCodeNumber(rawRespCode);
                    
                    const rawRoleCode = item.responsibilityAssignments?.role_code || '';
                    const cleanRoleCode = extractCodeNumber(rawRoleCode);
                    
                    const rawPositionCode = item.responsibilityAssignments?.position_code || '';
                    const cleanPositionCode = extractCodeNumber(rawPositionCode);
                    
                    transformedData.push({
                        id: item.id,
                        user: {
                            id: item.user || item.id,
                            name: `${item.first_name} ${item.last_name}`,
                            email: item.email || '',
                            avatar: item.avatar || null,
                            position: item.primary_position?.name || '',
                            department: '',
                        },
                        responsibilityAssignments: {
                            responsibility_id: item.responsibilityAssignments?.responsibility_id || '',
                            responsibility_code: cleanRespCode ? `#${cleanRespCode}` : rawRespCode,
                            responsibility_name: item.responsibilityAssignments?.responsibility_name || '',
                            responsibility_importance: item.responsibilityAssignments?.responsibility_importance || 3,
                            role_id: item.responsibilityAssignments?.role_id || '',
                            role_code: cleanRoleCode ? `#${cleanRoleCode}` : rawRoleCode,
                            role_name: item.responsibilityAssignments?.role_name || '',
                            role_importance: item.responsibilityAssignments?.role_importance || 3,
                            position_id: item.responsibilityAssignments?.position_id || '',
                            position_code: cleanPositionCode ? `#${cleanPositionCode}` : rawPositionCode,
                            position_name: item.responsibilityAssignments?.position_name || '',
                            position_importance: item.responsibilityAssignments?.position_importance || 3,
                            position_commitment: item.responsibilityAssignments?.position_commitment || 'Primary',
                            assigned_on: item.primary_position?.effective_on || null,
                            new_duration: 15,
                        },
                        badge: {
                            id: item.badge?.id || currentBadge?.id || null,
                            level: item.badge?.level || (currentBadge?.level && currentBadge.level !== 'None' 
                                ? currentBadge.level.toLowerCase() 
                                : 'Novice'),
                            status: item.badge?.status || (currentBadge?.status && currentBadge.status !== 'None' 
                                ? currentBadge.status 
                                : 'No Dot'),
                            checkpoints: item.badge?.checkpoints || currentBadge?.checkpoints || [],
                            issued_on: currentBadge?.issued_on || null,
                            issued_method: currentBadge?.issued_method || null,
                        },
                        inspectionStatus: item.inspectionStatus || 'Pending',
                        lastInspection: item.lastInspection || null,
                        checkpointCounts: item.checkpointCounts || {
                            apprentice_checkpoint_count: 0,
                            apprentice_passed_checkpoint_count: 0,
                            professional_checkpoint_count: 0,
                            professional_passed_checkpoint_count: 0,
                            coach_checkpoint_count: 0,
                            coach_passed_checkpoint_count: 0,
                            master_checkpoint_count: 0,
                            master_passed_checkpoint_count: 0,
                        },
                        originalEmployee: item,
                    });
                }
            });

            employees.value = transformedData;
            totalCount.value = response.data.count || transformedData.length;
            showPagination.value = totalCount.value > perPage.value;
            
            // Set banner data when employees are fetched
            if (totalCount.value > 0) {
                bannerData.value = {
                    title: `You currently have ${totalCount.value} potential ${totalCount.value === 1 ? 'responsibility' : 'responsibilities'} to inspect`,
                    description: 'When employees are ready for inspection, they should reach out to you to schedule it.',
                    buttonText: null,
                    icon: 'info_icon',
                };
            } else {
                bannerData.value = {
                    title: null,
                    description: null,
                    buttonText: null,
                    icon: null,
                };
            }
        }
    } catch (error) {
        console.error('Error fetching employees to inspect:', error.message);
        employees.value = [];
        totalCount.value = 0;
        showPagination.value = false;
        bannerData.value = {
            title: null,
            description: null,
            buttonText: null,
            icon: null,
        };
    } finally {
        proxy.$loader.hide();
    }
};

const handleIssueBadgeClick = async (event) => {
    const { data, action } = event;
    currentAction.value = action;
    
    const employeeData = data.originalEmployee || data;
    
    const respCode = data.responsibilityAssignments?.responsibility_code;
    const currentBadge = employeeData.badges?.find(b => 
        b.is_current && b.issued_for?.code === respCode
    ) || employeeData.badges?.find(b => b.is_current);
    
    selectedEmployee.value = {
        id: employeeData.employee_id || employeeData.id || data.id,
        first_name: employeeData.first_name || data.user?.name?.split(' ')[0] || '',
        last_name: employeeData.last_name || data.user?.name?.split(' ').slice(1).join(' ') || '',
        fullName: `${employeeData.first_name || ''} ${employeeData.last_name || ''}`.trim() || data.user?.name || '',
        avatar: employeeData.avatar || data.user?.avatar,
        email: employeeData.email || data.user?.email,
        badges: employeeData.badges || [],
        access: {
            is_suspended: employeeData.is_suspended || false
        }
    };
    
    const cleanRespCode = extractCodeNumber(respCode) || respCode?.replace(/^#/, '')?.split(' - ')[0] || '';
    
    selectedResponsibility.value = {
        id: data.responsibilityAssignments?.responsibility_id || employeeData.responsibility_id || currentBadge?.issued_for?.id || cleanRespCode,
        name: data.responsibilityAssignments?.responsibility_name || employeeData.responsibility_name || currentBadge?.issued_for?.name || '',
        code: cleanRespCode ? `#${cleanRespCode}` : (currentBadge?.issued_for?.code || '')
    };

    const checkpointCounts = data.checkpointCounts;
    let totalCheckpoints = 0;

    if (checkpointCounts) {
        const levels = ['apprentice', 'professional', 'coach', 'master'];
        totalCheckpoints = levels.reduce((total, level) => {
            return total + (checkpointCounts[`${level}_checkpoint_count`] || 0);
        }, 0);
    }

    activeCheckpoints.value = 0;
    
    if (action === 'Inspect') {
        const employeeId = selectedEmployee.value.id;
        const responsibilityId = selectedResponsibility.value.id;
        proxy.$router.push({
            name: 'ChecklistInspection',
            params: {
                employeeId: employeeId,
                responsibilityId: responsibilityId
            }
        });
        return;
    }
    
    if (showEmployeeModal.value) {
        showEmployeeModal.value = false;
        await nextTick();
    }
    
    employeeModalStep.value = 2;
    await nextTick();
    showEmployeeModal.value = true;
};

const closeEmployeeModal = () => {
    showEmployeeModal.value = false;
    selectedEmployee.value = null;
    selectedResponsibility.value = null;
    currentAction.value = '';
    activeCheckpoints.value = 0;
};

const handleGiveBadge = async (event) => {
    try {
        proxy.$loader.show();
        const { employee, radioVal, notifyEmployee } = event;
        const accountID = localStorage.getItem("currentAccountID");
        
        if (!accountID || !selectedEmployee.value || !selectedResponsibility.value) {
            console.error('Missing required data for giving badge');
            proxy.$loader.hide();
            return;
        }

        const payload = {
            employee: selectedEmployee.value.id,
            responsibility: selectedResponsibility.value.id,
            level: radioVal,
            forced: true,
            notify: notifyEmployee !== undefined ? notifyEmployee : true
        };
        
        await createProficiencyBadge(accountID, payload);
        
        closeEmployeeModal();
        await fetchEmployeesToInspect();
        
    } catch (error) {
        console.error('Error giving badge:', error);
        proxy.$loader.hide();
    }
};

const handleRemoveBadge = async (badge) => {
    try {
        closeEmployeeModal();
    } catch (error) {
        console.error('Error removing badge:', error);
    }
};

const handleBadgeRemoved = async () => {
    await fetchEmployeesToInspect();
};

const handleSearch = (searchText) => {
    searchQuery.value = searchText || "";
    
    if (searchTimeout) {
        clearTimeout(searchTimeout);
    }
    
    // If clearing search, fetch immediately without delay
    const delay = searchText === "" ? 0 : 500;
    
    searchTimeout = setTimeout(async () => {
        currentPage.value = 1;
        await fetchEmployeesToInspect();
    }, delay);
};

const handlePaginationRequest = async (paginationData) => {
    currentPage.value = paginationData.page;
    await fetchEmployeesToInspect();
};

const handleCheckedItemsChanged = (checkedItems) => {
    const readinessFilters = checkedItems
        .filter(item => {
            const value = typeof item === 'string' ? item : item.value;
            return ['novice', 'apprentice', 'professional'].includes(value?.toLowerCase());
        })
        .map(item => typeof item === 'string' ? item : item.value);
    
    badgeFilters.value = readinessFilters;

    filtersStore.setFilters({ 
        key: BADGE_FILTERS_KEY, 
        filters: badgeFilters.value 
    });
    
    currentPage.value = 1;
    fetchEmployeesToInspect();
};

const handleRemoveFilter = (removedFilter) => {
    const filterValue = removedFilter.toLowerCase();
    badgeFilters.value = badgeFilters.value.filter(f => f.toLowerCase() !== filterValue);
    filtersStore.setFilters({ 
        key: BADGE_FILTERS_KEY, 
        filters: badgeFilters.value 
    });
    
    currentPage.value = 1;
    fetchEmployeesToInspect();
};

const handleClearSelection = () => {
    badgeFilters.value = [];
    filtersStore.setFilters({ 
        key: BADGE_FILTERS_KEY, 
        filters: [] 
    });
    currentPage.value = 1;
    fetchEmployeesToInspect();
};

defineExpose({
    employees,
    fetchEmployeesToInspect,
    totalCount,
    currentPage,
    showPagination,
    perPage,
    badgeFilters,
    bannerData
});
</script>

<style scoped>
.your-employees-to-inspect-tab-wrapper {
    border-radius: 8px;
    border: 1px solid #EAECF0;
    background: #FFF;
    box-shadow: 0 1px 3px 0 rgba(16, 24, 40, 0.10), 0 1px 2px 0 rgba(16, 24, 40, 0.06);
}

.your-employees-to-inspect-tab-wrapper.has-data {
    padding-top: 22px;
}

::v-deep(.input-search) {
    left: -12px !important;
}

/* Fade transition styles */
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
</style>